import { supabase } from '../lib/supabase';

export const productionService = {
  // Get all production batches with optional filters
  getAll: async (filters = {}) => {
    try {
      let query = supabase?.from('production_batches')?.select('*, machines(machine_name, machine_code, status)');
      
      if (filters?.status && filters?.status !== 'all') {
        query = query?.eq('status', filters?.status);
      }
      if (filters?.priority && filters?.priority !== 'all') {
        query = query?.eq('priority', filters?.priority);
      }
      if (filters?.machine && filters?.machine !== 'all') {
        query = query?.eq('machine_id', filters?.machine);
      }
      if (filters?.deadline && filters?.deadline !== 'all') {
        const now = new Date();
        if (filters?.deadline === 'overdue') {
          query = query?.lt('deadline', now?.toISOString());
        } else if (filters?.deadline === 'upcoming') {
          const nextWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
          query = query?.gte('deadline', now?.toISOString())?.lte('deadline', nextWeek?.toISOString());
        }
        
      }
      
      const { data, error } = await query?.order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Convert snake_case to camelCase
      return data?.map(batch => ({
        id: batch?.id,
        batchNumber: batch?.batch_number,
        patientName: batch?.patient_name,
        clinicName: batch?.clinic_name,
        status: batch?.status,
        priority: batch?.priority,
        machineId: batch?.machine_id,
        machine: batch?.machines,
        progress: batch?.progress,
        startDate: batch?.start_date,
        deadline: batch?.deadline,
        case_id: batch?.case_id,
        completionDate: batch?.completion_date,
        notes: batch?.notes,
        createdAt: batch?.created_at,
        updatedAt: batch?.updated_at,
        current_phase: batch?.current_phase,
        model_up_from: batch?.model_up_from,
        model_up_to: batch?.model_up_to,
        model_low_from: batch?.model_low_from,
        model_low_to: batch?.model_low_to,
        label_from: batch?.label_from,
        label_to: batch?.label_to,
        responsible: batch?.responsible,
        qr_code_data: batch?.qr_code_data,
        delivery_qr_code_data: batch?.delivery_qr_code_data
      })) || [];
    } catch (error) {
      console.error('Error fetching production batches:', error);
      throw error;
    }
  },

  // Get single production batch by ID
  getById: async (id) => {
    try {
      const { data, error } = await supabase?.from('production_batches')?.select('*, machines(machine_name, machine_code, status)')?.eq('id', id)?.single();

       
      if (error) throw error;
      
      // Convert snake_case to camelCase
      return {
        id: data?.id,
        batchNumber: data?.batch_number,
        patientName: data?.patient_name,
        clinicName: data?.clinic_name,
        status: data?.status,
        priority: data?.priority,
        machineId: data?.machine_id,
        machine: data?.machines,
        progress: data?.progress,
        startDate: data?.start_date,
        responsible: data?.responsible,
        deadline: data?.deadline,
        completionDate: data?.completion_date,
        case_id: data?.case_id,
        notes: data?.notes,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at,
        current_phase: data?.current_phase,
        model_up_from: data?.model_up_from,
        model_up_to: data?.model_up_to,
        model_low_from: data?.model_low_from,
        model_low_to: data?.model_low_to,
        label_from: data?.label_from,
        label_to: data?.label_to,
        elastic: data?.elastic,
        ali_confirmation: data?.ali_confirmation,
        suppression: data?.suppression,
        of_number: data?.of_number,
        packaging_date: data?.packaging_date,
        qr_code_data: data?.qr_code_data,
        delivery_qr_code_data: data?.delivery_qr_code_data,
      };
    } catch (error) {
      console.error(`Error fetching production batch ${id}:`, error);
      throw error;
    }
  },

  // Create new production batch
  create: async (batchData) => {
    try {
      const { data, error } = await supabase?.from('production_batches')?.insert({
          batch_number: batchData?.batchNumber,
          patient_name: batchData?.patientName,
          clinic_name: batchData?.clinicName,
          status: batchData?.status || 'pending',
          priority: batchData?.priority || 'medium',
          machine_id: batchData?.machineId || null,
          progress: batchData?.progress || 0,
          start_date: batchData?.startDate || null,
          deadline: batchData?.deadline,
          notes: batchData?.notes || null
        })?.select()?.single();
      
      if (error) throw error;
      
      // Convert snake_case to camelCase
      return {
        id: data?.id,
        batchNumber: data?.batch_number,
        patientName: data?.patient_name,
        clinicName: data?.clinic_name,
        status: data?.status,
        priority: data?.priority,
        machineId: data?.machine_id,
        progress: data?.progress,
        startDate: data?.start_date,
        deadline: data?.deadline,
        completionDate: data?.completion_date,
        notes: data?.notes,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error creating production batch:', error);
      throw error;
    }
  },

  // Update existing production batch
  update: async (id, batchData) => {
    try {
      const updateData = {};
      if (batchData?.batchNumber !== undefined) updateData.batch_number = batchData?.batchNumber;
      if (batchData?.patientName !== undefined) updateData.patient_name = batchData?.patientName;
      if (batchData?.clinicName !== undefined) updateData.clinic_name = batchData?.clinicName;
      if (batchData?.status !== undefined) updateData.status = batchData?.status;
      if (batchData?.priority !== undefined) updateData.priority = batchData?.priority;
      if (batchData?.machineId !== undefined) updateData.machine_id = batchData?.machineId;
      if (batchData?.progress !== undefined) updateData.progress = batchData?.progress;
      if (batchData?.startDate !== undefined) updateData.start_date = batchData?.startDate;
      if (batchData?.deadline !== undefined) updateData.deadline = batchData?.deadline;
      if (batchData?.completionDate !== undefined) updateData.completion_date = batchData?.completionDate;
      if (batchData?.notes !== undefined) updateData.notes = batchData?.notes;
      
      const { data, error } = await supabase?.from('production_batches')?.update(updateData)?.eq('id', id)?.select()?.single();
      
      if (error) throw error;
      
      // Convert snake_case to camelCase
      return {
        id: data?.id,
        batchNumber: data?.batch_number,
        patientName: data?.patient_name,
        clinicName: data?.clinic_name,
        status: data?.status,
        priority: data?.priority,
        machineId: data?.machine_id,
        progress: data?.progress,
        startDate: data?.start_date,
        deadline: data?.deadline,
        completionDate: data?.completion_date,
        notes: data?.notes,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error(`Error updating production batch ${id}:`, error);
      throw error;
    }
  },

  // Delete production batch
  delete: async (id) => {
    try {
      const { error } = await supabase?.from('production_batches')?.delete()?.eq('id', id);
      
      if (error) throw error;
      
      return { success: true };
    } catch (error) {
      console.error(`Error deleting production batch ${id}:`, error);
      throw error;
    }
  },

  // Update batch status
  updateStatus: async (id, status) => {
    try {
      const { data, error } = await supabase?.from('production_batches')?.update({ status })?.eq('id', id)?.select()?.single();
      
      if (error) throw error;
      
      return {
        id: data?.id,
        status: data?.status,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error(`Error updating batch status ${id}:`, error);
      throw error;
    }
  },

  // Update batch progress
  updateProgress: async (id, progress) => {
    try {
      const { data, error } = await supabase?.from('production_batches')?.update({ progress })?.eq('id', id)?.select()?.single();
      
      if (error) throw error;
      
      return {
        id: data?.id,
        progress: data?.progress,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error(`Error updating batch progress ${id}:`, error);
      throw error;
    }
  },

  // Generate QR code data for batch
  generateQRCode: (batch) => {
    return `LOT-${batch?.batchNumber}-${Date.now()}`;
  },

  // Generate delivery QR code
  generateDeliveryQRCode: (batch) => {
    return `DELIVERY-${batch?.batchNumber}-${Date.now()}`;
  },

  // Update batch with QR tracking
  updateWithQRTracking: async (id, qrData, operatorId, phase, notes) => {
    try {
      const updateData = {
        notes: notes || null,
        updated_at: new Date()?.toISOString()
      };

      const { data, error } = await supabase?.from('production_batches')?.update(updateData)?.eq('id', id)?.select()?.single();
      
      if (error) throw error;
      
      return {
        id: data?.id,
        notes: data?.notes,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error(`Error updating batch with QR tracking ${id}:`, error);
      throw error;
    }
  },
};

export default productionService;